import { calculateInvoice, InvoiceNotFoundForCalculation } from "../../worker/invoices/calculation";
import { parseInvoiceDraftInput } from "../../worker/invoices/input";
import { InvoiceInputError, type InvoiceTaxRate } from "../../worker/invoices/types";
import type { DraftForm } from "./InvoiceDocument";

export type DraftCalculation =
  | { status: "valid"; value: ReturnType<typeof calculateInvoice> }
  | { status: "invalid"; error: InvoiceInputError | InvoiceNotFoundForCalculation };

export function calculateDraftPreview(form: DraftForm, kind: "invoice" | "estimate", rates: Map<string, InvoiceTaxRate>): DraftCalculation {
  try {
    // Validate only the financial inputs. Unfinished document metadata does not
    // affect the preview, and the canonical parser owns numeric/tax validation.
    const input = parseInvoiceDraftInput({
      clientId: null, recipientContactId: null, issueDate: "2000-01-01", note: null,
      discount: form.discountKind === "none" ? null : { kind: form.discountKind, value: form.discountValue, label: null },
      ...(form.taxModelVersion === 1 ? { taxModelVersion: 1, taxes: form.taxes } : kind === "invoice" ? { defaultTaxRateId: form.defaultTaxRateId || null } : {}),
      lines: form.lines.map(line => ({
        description: line.description, quantity: line.quantity, rate: line.rate,
        taxRateId: form.taxModelVersion === 1 ? null : line.taxMode === "override" ? line.taxRateId || null : kind === "estimate" ? line.taxRateId || null : null,
        ...(form.taxModelVersion === 1 || kind === "invoice" ? { taxMode: line.taxMode } : {}),
        ...(form.taxModelVersion === 1 ? { taxSet: line.taxSet ?? [] } : {}),
      })),
    });
    const lines = input.lines.map((line, index) => ({ ...line, description: form.lines[index]!.description }));
    const discount = input.discount ? { ...input.discount, label: form.discountLabel || null } : null;
    return { status: "valid", value: calculateInvoice(lines, discount, rates, input.defaultTaxRateId ?? null, input.taxes) };
  } catch (error) {
    if (error instanceof InvoiceInputError || error instanceof InvoiceNotFoundForCalculation) return { status: "invalid", error };
    throw error;
  }
}

/** Same canonical parser as Save; only the placement and wording of its errors differ. */
export function draftLineErrors(form: DraftForm): Record<string, {quantity?: string; rate?: string}> {
  const errors: Record<string, {quantity?: string; rate?: string}> = {};
  for (const line of form.lines) {
    // Validate each field through the same parser, so a missing rate cannot
    // obscure an invalid quantity (or incorrectly mark both fields invalid).
    for (const field of ["quantity","rate"] as const) {
      try {
        parseInvoiceDraftInput({clientId:null,recipientContactId:null,issueDate:"2000-01-01",note:null,discount:null,
          lines:[{description:"",quantity:field === "quantity" ? line.quantity : "1",rate:field === "rate" ? line.rate : "0",taxRateId:null}]});
      } catch (error) {
        if (!(error instanceof InvoiceInputError)) throw error;
        const message = !line[field] ? `Enter a ${field}.`
          : field === "quantity" ? Number(line.quantity) > 99_999.999 ? "Quantity must be 99,999.999 or less, with up to 3 decimal places."
            : "Enter a quantity greater than 0, with up to 3 decimal places."
          : Math.abs(Number(line.rate)) > 99_999_999.99 ? "Rate must be between −99,999,999.99 and 99,999,999.99."
          : "Enter a valid rate, with up to 2 decimal places.";
        // A wholly empty saved draft line remains supported by the parser.
        if (line.quantity || line.rate) (errors[line.key] ??= {})[field] = message;
      }
    }
  }
  return errors;
}
